import { useState, useRef, useEffect } from 'react';

import { toast } from 'common/helpers/toastConfig';
import { useActions } from 'common/hooks/useActions';
import Loader from 'components/loader';

import * as S from './styles';

interface ImageUploaderProps {
  setImage: (image: string | null) => void;
  defaultImage?: string | null;
  setValues: any;
  values: any;
}

const MAX_FILE_SIZE_MB = 2;

const AddIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round">
    <rect x="3" y="4" width="18" height="16" rx="2" />
    <circle cx="9" cy="10" r="2" />
    <path d="M21 16l-5-5-6 6" />
  </svg>
);

const ImageUploader: React.FC<ImageUploaderProps> = ({ setImage, defaultImage, setValues, values }) => {
  const [isUploading] = useState(false);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  /*
   * Every menu photo in this environment is an imgproxy URL that 404s, and a
   * bare <img> against a 404 drew the browser's broken-image glyph inside the
   * control — so the uploader looked broken rather than empty. On an error it
   * falls back to the add state, which is both honest and the thing you want
   * to click.
   */
  const [failed, setFailed] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { cafeteriaUploadRequestAction } = useActions();

  useEffect(() => setFailed(false), [defaultImage]);

  const handleFileInputChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const file = files[0];

    const fileSizeMB = file.size / (1024 * 1024);
    if (fileSizeMB > MAX_FILE_SIZE_MB) {
      toast({ model: 'error', message: 'File is too big. Maximum allowed size is 2MB.' });
      return;
    }

    const fileDataURL = await getFileDataURL(file);

    cafeteriaUploadRequestAction({ image: file }, (fileNameOnly: string) => {
      setValues({
        ...values,
        imageName: file,
        fileUrl: fileDataURL,
        name: fileNameOnly
      });
      setImage(values.fileUrl);
    });
  };

  const getFileDataURL = (file: File) => {
    return new Promise<string>((resolve) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        resolve(reader.result as string);
        setLogoPreview(reader.result as string);
      };
    });
  };

  const source = logoPreview || (failed ? null : defaultImage);

  return (
    <S.Uploader htmlFor="fileInput" className={source ? 'filled' : ''} title="Upload an image">
      <input
        id="fileInput"
        type="file"
        accept=".png, .jpg, .jpeg, .heic"
        onChange={handleFileInputChange}
        ref={fileInputRef}
        style={{ display: 'none' }}
      />
      {isUploading ? (
        <Loader />
      ) : source ? (
        <S.Image src={source} alt="" onError={() => setFailed(true)} />
      ) : (
        <AddIcon />
      )}
    </S.Uploader>
  );
};

export default ImageUploader;
